import * as React from 'react';
import {
  Slider as RACSlider,
  SliderTrack as RACSliderTrack,
  SliderThumb as RACSliderThumb,
  type SliderProps as RACSliderProps,
} from 'react-aria-components';

import { cn } from '../lib/cn';

/**
 * Klaret Slider (handoff §10.3).
 * Track: 4px --tvl-inset groove; filled portion --tvl-accent.
 * Thumb: 16px r-full --tvl-raised circle, --tvl-border-input border, --tvl-e1.
 * Supports single and range (multiple thumbs via value/defaultValue arrays).
 * Focus = global 2px ink outline (globals.css) — no accent ring.
 */
export const Slider = React.forwardRef<HTMLDivElement, RACSliderProps<number | number[]>>(
  ({ className, value, defaultValue, ...props }, ref) => {
    // Determine thumb count from value/defaultValue (default 1)
    const thumbCount = Array.isArray(value)
      ? value.length
      : Array.isArray(defaultValue)
        ? defaultValue.length
        : 1;

    return (
      <RACSlider
        ref={ref}
        value={value}
        defaultValue={defaultValue ?? 0}
        className={cn('relative flex w-full touch-none select-none items-center', className)}
        {...props}
      >
        <RACSliderTrack className="relative flex h-4 w-full items-center">
          {({ state }) => {
            // For a single thumb: fill from 0 to thumb position
            // For range: fill between first and last thumb
            const pct0 = thumbCount > 1 ? state.getThumbPercent(0) * 100 : 0;
            const pct1 =
              thumbCount > 1
                ? state.getThumbPercent(thumbCount - 1) * 100
                : state.getThumbPercent(0) * 100;
            return (
              <>
                {/* inset track groove — vertically centred in the h-4 track */}
                <span className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-[var(--tvl-r-full)] bg-[var(--tvl-inset)]" />
                {/* accent filled range */}
                <span
                  className="absolute top-1/2 h-1 -translate-y-1/2 rounded-[var(--tvl-r-full)] bg-[var(--tvl-accent)]"
                  style={{ left: `${pct0}%`, width: `${pct1 - pct0}%` }}
                />
                {/* thumbs */}
                {Array.from({ length: thumbCount }).map((_, i) => (
                  <RACSliderThumb
                    key={i}
                    index={i}
                    className={cn(
                      'block h-4 w-4 rounded-[var(--tvl-r-full)]',
                      'border border-[var(--tvl-border-input)] bg-[var(--tvl-raised)]',
                      'shadow-[var(--tvl-e1)]',
                      'transition-colors duration-[var(--tvl-d-fast)]',
                      'dragging:border-[var(--tvl-accent)]',
                      'disabled:pointer-events-none disabled:opacity-50',
                    )}
                  />
                ))}
              </>
            );
          }}
        </RACSliderTrack>
      </RACSlider>
    );
  },
);
Slider.displayName = 'Slider';
